import { useTranslation } from 'react-i18next'
import { X, Info, AlertTriangle, AlertCircle, CheckCircle } from 'lucide-react'
import { BannerData } from '@/services/bannerService'

interface BannerProps {
  banner: BannerData
  onDismiss?: (bannerId: string) => void
  isDismissing?: boolean
}

export function Banner({ banner, onDismiss, isDismissing = false }: BannerProps) {
  const { t: tc } = useTranslation('common')

  const getTypeStyles = () => {
    switch (banner.type) {
      case 'warning':
        return {
          bg: 'bg-yellow-50',
          border: 'border-yellow-400',
          text: 'text-yellow-800',
          icon: <AlertTriangle className="h-5 w-5 text-yellow-600" />
        }
      case 'error':
        return {
          bg: 'bg-red-50',
          border: 'border-red-400',
          text: 'text-red-800',
          icon: <AlertCircle className="h-5 w-5 text-red-600" />
        }
      case 'success':
        return {
          bg: 'bg-green-50',
          border: 'border-green-400',
          text: 'text-green-800',
          icon: <CheckCircle className="h-5 w-5 text-green-600" />
        }
      case 'info':
      default:
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-400',
          text: 'text-blue-800',
          icon: <Info className="h-5 w-5 text-blue-600" />
        }
    }
  }

  const styles = getTypeStyles()

  return (
    <div className={`${styles.bg} ${styles.border} border-b`}>
      <div className="max-w-7xl mx-auto py-3 px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between flex-wrap">
          <div className="flex items-center flex-1">
            <span className="flex p-2 rounded-lg">
              {styles.icon}
            </span>
            <p className={`ml-3 font-medium ${styles.text}`}>
              {banner.message}
            </p>
            {banner.link && banner.linkText && (
              <a
                href={banner.link}
                className={`ml-3 font-semibold ${styles.text} underline hover:opacity-80`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {banner.linkText} →
              </a>
            )}
          </div>
          {banner.dismissible && onDismiss && (
            <button
              onClick={() => onDismiss(banner.id)}
              disabled={isDismissing}
              className={`flex-shrink-0 ml-4 p-1 rounded-md hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50 ${styles.text}`}
              aria-label={tc('dismissBanner')}
              aria-busy={isDismissing}
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
